import logger from "@/lib/logger";

// Bridge between the web app and the native Capacitor shell for the OS share sheet.
//
// Why this exists: `shareUrl()` selects the OS sheet on `navigator.share` alone, and the Android System
// WebView is BELIEVED not to implement the Web Share API — so inside `com.seavien.mobile` every share
// would fall through to the desktop fallback drawer and then the clipboard, on the one surface where
// the OS sheet is the native, expected gesture. That belief is a HYPOTHESIS, not a measurement: Stage 0
// of the design is the on-device probe and it has not been run. Nothing here depends on which way it
// goes — the branch is a capability check either way, and if the WebView does expose `navigator.share`
// this branch simply wins first with the plugin we can actually reason about (design D1).
// Design: designs/01-now/2026-07-30-native-share-sheet-capacitor-bridge-design.md.
//
// The plugin is registered by the native shell, so we reach it through `window.Capacitor.Plugins`
// rather than importing `@capacitor/*` into the front-end build — the §1 architectural rule of the seam
// reference, and what keeps this module's bundle-budget cost at zero: nothing enters the eager graph.
//
// UNLIKE the other three bridges, this one is NOT installed from `runOneTimeGlobalSetup`. It registers
// no listeners and owns no lifecycle, so it has no setup and no teardown; it is called on demand from
// `shareUrl()` and nowhere else.
//
// NO FEATURE FLAG, deliberately (design D2, AGENTS.md rule 35). The fallback chain IS the flag: a
// missing plugin is inert here — `window.Capacitor.Plugins.Share` is simply `undefined`, no native
// throw crosses the bridge, and there is no config file whose absence crashes the process. That is the
// difference from push, and it is why an old APK running a freshly deployed bundle degrades to exactly
// today's behaviour rather than needing a compiled constant to protect it.

/** What the OS sheet did. See {@link shareViaNativeShell} — the discrimination is the point. */
export type NativeShareOutcome = "shared" | "dismissed" | "unavailable";

/**
 * `{title, text, url}`. Files are out of scope for this slice, and `text` must never be `""` — an empty
 * value still produces a leading blank line in some Android targets, so the caller omits it instead.
 */
export type NativeSharePayload = {
    title?: string;
    text?: string;
    url: string;
    /**
     * The chooser's own heading. Passing it is NOT cosmetic: `SharePlugin.java:84` defaults it to the
     * hardcoded English string `"Share"`, so omitting it ships an English chooser title to 24 locales.
     */
    dialogTitle?: string;
};

type CapacitorSharePlugin = {
    share?: (options: NativeSharePayload) => Promise<{ activityType?: string } | undefined>;
};

type CapacitorPluginsRuntime = {
    Share?: CapacitorSharePlugin;
};

function getCapacitorPlugins(): CapacitorPluginsRuntime | undefined {
    if (typeof window === "undefined") {
        return undefined;
    }

    return (window.Capacitor as { Plugins?: CapacitorPluginsRuntime } | undefined)?.Plugins;
}

/**
 * Present the OS share sheet, or report that this runtime cannot.
 *
 * **The return type is the whole point of this module.** A Capacitor rejection is an ordinary `Error`
 * whose `name` is NOT `"AbortError"`, and `shareUrl()`'s catch block treats everything that is not an
 * `AbortError` as a failure — `logger.error` plus a `share_url.share_failed` toast. Letting a plugin
 * rejection reach that catch would tell every user who backs out of the Android chooser that sharing
 * failed, on the most ordinary interaction in the flow. So this function never rejects: it resolves to
 * one of three outcomes and swallows the rest.
 *
 * **Every rejection is `"dismissed"`, not just the recognised ones.** There are four sources, confirmed
 * against `SharePlugin.java` (7.0.4) rather than assumed:
 *  - the user backs out of the chooser  → `"Share canceled"` (`:61`);
 *  - a second `share()` while one is presenting → `"Can't share while sharing is in progress"` (`:138`);
 *  - a `url` that is neither `http(s):` nor `file:` → `"Unsupported url"` (`:92`);
 *  - a plugin that throws synchronously rather than returning a promise (an old/partial shim).
 * Matching on the message would leave the last two as errors, and an error here means a toast. The
 * chooser is a modal the user controls, so silence is the right failure mode and a false `"dismissed"`
 * is strictly better than a false "sharing failed".
 *
 * `"unavailable"` is the OLD-APK case and it is load-bearing: the web bundle is served remotely, so a
 * freshly deployed bundle runs inside APKs installed weeks earlier that do not contain
 * `@capacitor/share`. The caller must fall through to its existing chain untouched — never treat this
 * as an error and never emit a share event for it.
 *
 * Gating is on plugin PRESENCE, never on `getPlatform()` and never on `Share.canShare()`:
 * `SharePlugin.java:71-75` resolves a hardcoded `{value: true}` regardless of anything, so it would
 * answer `true` on a device with no share targets at all. Presence-gating also means iOS inherits this
 * for free on the day its target ships, with no edit here.
 */
export async function shareViaNativeShell(payload: NativeSharePayload): Promise<NativeShareOutcome> {
    const sharePlugin = getCapacitorPlugins()?.Share;
    if (typeof sharePlugin?.share !== "function") {
        return "unavailable";
    }

    try {
        // `await` inside the try, not a returned promise: a synchronous throw and a rejected promise
        // must land in the same catch, and one of the four rejection sources is the synchronous one.
        await sharePlugin.share(payload);
        return "shared";
    } catch (error) {
        try {
            // Debug, never error: a dismissal is a normal user action. `logger.error` here would put a
            // routine gesture into the frontend-island-crash signal and train everyone to ignore it.
            logger.debug("Native share sheet did not complete", error);
        } catch {
            // ⚠️ Not defensive clutter. This function's ONE guarantee is that it never throws, and an
            // unguarded log call inside the catch is the single line that can break it — at which point
            // the throw lands in `shareUrl()`'s catch and raises `share_url.share_failed`, i.e. exactly
            // the user-visible defect this module exists to prevent, now caused by the instrumentation
            // rather than by the plugin. Observed for real on 2026-08-01: a caller's partial `logger`
            // mock (error only, no debug) turned every dismissal into a "Share failed" toast.
            // Same reasoning as `emitShareInitiated`'s outer try/catch in PlatformIdentifierUtil.
        }
        return "dismissed";
    }
}
